import type { Time } from "@suse-o11y/date-time/time";
import { DialogBoundary, DialogTrigger } from "@suse-o11y/dialog";
import { IconEllipsisOutlined, IconEnlarge } from "@suse-o11y/icons";
import { mergeRefs } from "@suse-o11y/merge-refs";
import { Template } from "@suse-o11y/template";
import { Tooltip, TooltipBoundary, TooltipTrigger } from "@suse-o11y/tooltip";
import React from "react";
import { useMeasure } from "react-use";

import { DropdownMenu } from "~/components/dropdown-menu";
import { DropdownMenuContent } from "~/components/dropdown-menu/content";
import { DropdownMenuItem } from "~/components/dropdown-menu/item";
import { DropdownMenuTrigger } from "~/components/dropdown-menu/trigger";
import { SkeletonBox } from "~/components/skeleton/box";
import type { MetricQuery } from "~/models/metric-query";

import type { TimeRange } from "../../app/state/view/timeline/TimeRange";
import { normalizePinnedMetricComponent } from "../../bottom_content/view/panes/east/pinned-items/PinnedItem";
import { PinningButton } from "../../bottom_content/view/panes/east/pinned-items/PinningButton";
import { DashboardChartPluginSpec, DashboardWidgetSpec } from "../../common/dashboard-spec";
import { TimeSeriesQuery } from "../../common/dashboard-spec/_time-series-query";
import { AddToDashboardDrawer } from "../_add-to-dashboard-drawer";
import { useComponentBoundary } from "../_component-boundary";
import { MetricsDrawer } from "../metrics-drawer";
import { EmptyChart } from "../react/chart/chart-states/EmptyChart";
import { ErrorChart } from "../react/chart/chart-states/ErrorChart";

import { fromMetricQuery as widgetQueryFromMetricQuery } from "./query/spec";
import { WidgetLayout, type WidgetLayoutProps } from "./_widget-layout";
import type { Spec } from "./spec";
import { type UseQueryTimeSeriesParams, useQueryTimeSeries } from "./use-query-time-series";

import { Chart, type ChartProps } from ".";

export interface ChartWidgetProps
	extends Pick<WidgetLayoutProps, "className" | "title" | "description">,
		Pick<UseQueryTimeSeriesParams, "namespace" | "retry" | "batch">,
		Pick<ChartProps, "onTimeRangeChange"> {
	queries: ReadonlyArray<MetricQuery>;
	chart: Spec;
	timestamp: undefined | Time;
	timeRange: TimeRange;
}

export const ChartWidget = React.forwardRef<React.ComponentRef<"div">, ChartWidgetProps>(
	(
		{ className, title, description, namespace, retry, batch, queries, chart, timestamp, timeRange, onTimeRangeChange },
		ref,
	) => {
		const [measureRef, { width }] = useMeasure<HTMLDivElement>();

		const component = normalizePinnedMetricComponent(useComponentBoundary());

		const result = useQueryTimeSeries({
			namespace,
			retry,
			batch,
			width: chart.kind === "StatChart" || chart.kind === "GaugeChart" || chart.kind === "PieChart" ? undefined : width,
			timeRange,
			queries,
		});

		return (
			<WidgetLayout
				actions={
					<div className="flex gap-2">
						{component != null && (
							<PinningButton item={{ name: title, description, metricQueries: queries, component, chart }} onlyIcon />
						)}

						<DialogBoundary>
							<TooltipBoundary>
								<TooltipTrigger asChild>
									<DialogTrigger
										className="button button-square button-xs"
										data-beest-testid="metric-chart-expand-button"
									>
										<IconEnlarge />
									</DialogTrigger>
								</TooltipTrigger>

								<Tooltip className="tooltip/tooltip">Open in metric preview</Tooltip>
							</TooltipBoundary>

							<MetricsDrawer
								chart={chart}
								metricQueries={queries}
								timeRange={timeRange}
								timestamp={timestamp}
								title={title}
							/>
						</DialogBoundary>

						<DialogBoundary>
							<DropdownMenu>
								<DropdownMenuTrigger asChild={false} className="button button-square button-xs">
									<IconEllipsisOutlined className="text-xl/4" />
								</DropdownMenuTrigger>

								<DropdownMenuContent align="end">
									<DialogTrigger asChild>
										{/* do not close the dropdown when opening the drawer */}
										<DropdownMenuItem onSelect={(event) => event.preventDefault()}>Add to dashboard</DropdownMenuItem>
									</DialogTrigger>
								</DropdownMenuContent>
							</DropdownMenu>

							<AddWidgetToDashboardDrawer chart={chart} description={description} queries={queries} title={title} />
						</DialogBoundary>
					</div>
				}
				className={className}
				description={description}
				ref={mergeRefs([ref, measureRef])}
				title={title}
			>
				{result.match({
					idle: () => <EmptyChart size="lg" />,

					loading: () => <SkeletonBox className="h-55" />,

					failure: ({ error }) => <ErrorChart error={error} size="lg" />,

					success: ({ value: { timeSeries } }) => {
						if (timeSeries.length === 0) {
							return <EmptyChart size="lg" />;
						}

						return (
							<Chart
								canvasHeight={200}
								fallback={<SkeletonBox className="h-55" />}
								onTimeRangeChange={onTimeRangeChange}
								showAllDialog={
									<MetricsDrawer
										chart={chart}
										metricQueries={queries}
										timeRange={timeRange}
										timestamp={timestamp}
										title={title}
									/>
								}
								spec={chart}
								timeRange={timeRange}
								timeSeries={timeSeries}
							/>
						);
					},
				})}
			</WidgetLayout>
		);
	},
);

const AddWidgetToDashboardDrawer: React.FC<Pick<ChartWidgetProps, "title" | "description" | "queries" | "chart">> = ({
	title,
	description,
	queries,
	chart,
}) => {
	const widget = React.useMemo(
		() =>
			DashboardWidgetSpec.of({
				name: Template(title ?? ""),
				description: description == null ? undefined : Template(description),
				plugin: {
					links: [],
					queries: queries.map((query) => TimeSeriesQuery.fromQuerySpec(widgetQueryFromMetricQuery(query))),
					...DashboardChartPluginSpec.fromChartSpec(chart),
				},
			}),
		[title, description, queries, chart],
	);

	return <AddToDashboardDrawer widgets={[widget]} />;
};
